import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const MaintenanceRecords = ({ asset, isEditing, onUpdate }) => {
  const [showAddRecord, setShowAddRecord] = useState(false);
  const [newRecord, setNewRecord] = useState({
    type: '',
    description: '',
    performedBy: '',
    cost: '',
    scheduledDate: '',
    completedDate: '',
    status: 'pending'
  });

  const maintenanceTypeOptions = [
    { value: 'preventive', label: 'Preventive Maintenance' },
    { value: 'corrective', label: 'Corrective Maintenance' },
    { value: 'emergency', label: 'Emergency Repair' },
    { value: 'upgrade', label: 'Hardware Upgrade' },
    { value: 'software', label: 'Software Update' },
    { value: 'cleaning', label: 'Cleaning/Inspection' },
    { value: 'calibration', label: 'Calibration' },
    { value: 'warranty', label: 'Warranty Service' }
  ];

  const statusOptions = [
    { value: 'pending', label: 'Pending' },
    { value: 'in-progress', label: 'In Progress' },
    { value: 'completed', label: 'Completed' },
    { value: 'cancelled', label: 'Cancelled' },
    { value: 'on-hold', label: 'On Hold' }
  ];

  const maintenanceRecords = asset?.maintenanceRecords || [
    {
      id: 1,
      type: 'preventive',
      description: 'Quarterly system cleaning and thermal paste replacement',
      performedBy: 'Tech Support Team',
      cost: 150.00,
      scheduledDate: '2025-08-15',
      completedDate: '2025-08-15',
      status: 'completed',
      notes: 'System running optimally after maintenance. No issues found.',
      attachments: ['maintenance_report_q3.pdf']
    },
    {
      id: 2,
      type: 'software',
      description: 'Windows 11 security updates and driver updates',
      performedBy: 'IT Administrator',
      cost: 0,
      scheduledDate: '2025-08-10',
      completedDate: '2025-08-10',
      status: 'completed',
      notes: 'All updates installed successfully. System reboot required.',
      attachments: []
    },
    {
      id: 3,
      type: 'upgrade',
      description: 'RAM upgrade from 8GB to 16GB',
      performedBy: 'Hardware Technician',
      cost: 120.00,
      scheduledDate: '2025-07-25',
      completedDate: '2025-07-25',
      status: 'completed',
      notes: 'Performance improvement verified. Memory test passed.',
      attachments: ['ram_upgrade_receipt.pdf', 'performance_test.pdf']
    },
    {
      id: 4,
      type: 'preventive',
      description: 'Annual hardware inspection and component testing',
      performedBy: 'External Service Provider',
      cost: 200.00,
      scheduledDate: '2025-09-01',
      completedDate: null,
      status: 'pending',
      notes: 'Scheduled for next month. Service provider confirmed.',
      attachments: []
    }
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'bg-success text-success-foreground';
      case 'in-progress':
        return 'bg-accent text-accent-foreground';
      case 'pending':
        return 'bg-warning text-warning-foreground';
      case 'cancelled':
        return 'bg-error text-error-foreground';
      case 'on-hold':
        return 'bg-secondary text-secondary-foreground';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'preventive':
        return 'Calendar';
      case 'corrective':
        return 'Wrench';
      case 'emergency':
        return 'AlertTriangle';
      case 'upgrade':
        return 'TrendingUp';
      case 'software':
        return 'Download';
      case 'cleaning':
        return 'Sparkles';
      case 'calibration':
        return 'Settings';
      case 'warranty':
        return 'Shield';
      default:
        return 'Tool';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not scheduled';
    return new Date(dateString)?.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const calculateTotalCost = () => {
    return maintenanceRecords?.filter(record => record?.status === 'completed')?.reduce((total, record) => total + (record?.cost || 0), 0);
  };

  const handleAddRecord = () => {
    if (newRecord?.type && newRecord?.description) {
      const record = {
        ...newRecord,
        id: Date.now(),
        cost: parseFloat(newRecord?.cost) || 0
      };
      
      const updatedRecords = [...maintenanceRecords, record];
      onUpdate({ ...asset, maintenanceRecords: updatedRecords });
      
      setNewRecord({
        type: '',
        description: '',
        performedBy: '',
        cost: '',
        scheduledDate: '',
        completedDate: '',
        status: 'pending'
      });
      setShowAddRecord(false);
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6 shadow-subtle">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-foreground">Maintenance Records</h3>
        {isEditing && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowAddRecord(!showAddRecord)}
            iconName="Plus"
            iconPosition="left"
          >
            Add Record
          </Button>
        )}
      </div>
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-muted rounded-lg p-4">
          <div className="flex items-center space-x-2 mb-2">
            <Icon name="CheckCircle" size={16} className="text-success" />
            <span className="text-sm font-medium text-foreground">Completed</span>
          </div>
          <p className="text-2xl font-semibold text-foreground">
            {maintenanceRecords?.filter(r => r?.status === 'completed')?.length}
          </p>
        </div>

        <div className="bg-muted rounded-lg p-4">
          <div className="flex items-center space-x-2 mb-2">
            <Icon name="Clock" size={16} className="text-warning" />
            <span className="text-sm font-medium text-foreground">Pending</span>
          </div>
          <p className="text-2xl font-semibold text-foreground">
            {maintenanceRecords?.filter(r => r?.status === 'pending')?.length}
          </p>
        </div>

        <div className="bg-muted rounded-lg p-4">
          <div className="flex items-center space-x-2 mb-2">
            <Icon name="DollarSign" size={16} className="text-accent" />
            <span className="text-sm font-medium text-foreground">Total Cost</span>
          </div>
          <p className="text-2xl font-semibold text-foreground">
            ₹{calculateTotalCost()?.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
          </p>
        </div>

        <div className="bg-muted rounded-lg p-4">
          <div className="flex items-center space-x-2 mb-2">
            <Icon name="Calendar" size={16} className="text-primary" />
            <span className="text-sm font-medium text-foreground">Last Service</span>
          </div>
          <p className="text-sm font-semibold text-foreground">
            {formatDate(maintenanceRecords?.filter(r => r?.status === 'completed' && r?.completedDate)?.sort((a, b) => new Date(b.completedDate) - new Date(a.completedDate))?.[0]?.completedDate
            )}
          </p>
        </div>
      </div>
      {/* Add New Record Form */}
      {showAddRecord && isEditing && (
        <div className="bg-muted rounded-lg p-4 mb-6">
          <h4 className="text-md font-medium text-foreground mb-4">Add Maintenance Record</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Select
              label="Maintenance Type"
              options={maintenanceTypeOptions}
              value={newRecord?.type}
              onChange={(value) => setNewRecord({ ...newRecord, type: value })}
              required
            />

            <Select
              label="Status"
              options={statusOptions}
              value={newRecord?.status}
              onChange={(value) => setNewRecord({ ...newRecord, status: value })}
            />

            <div className="md:col-span-2">
              <Input
                label="Description"
                type="text"
                value={newRecord?.description}
                onChange={(e) => setNewRecord({ ...newRecord, description: e?.target?.value })}
                placeholder="Describe the maintenance work"
                required
              />
            </div>

            <Input
              label="Performed By"
              type="text"
              value={newRecord?.performedBy}
              onChange={(e) => setNewRecord({ ...newRecord, performedBy: e?.target?.value })}
              placeholder="Technician or service provider"
            />

            <Input
              label="Cost ($)"
              type="number"
              value={newRecord?.cost}
              onChange={(e) => setNewRecord({ ...newRecord, cost: e?.target?.value })}
              placeholder="0.00"
            />

            <Input
              label="Scheduled Date"
              type="date"
              value={newRecord?.scheduledDate}
              onChange={(e) => setNewRecord({ ...newRecord, scheduledDate: e?.target?.value })}
            />

            <Input
              label="Completed Date"
              type="date"
              value={newRecord?.completedDate}
              onChange={(e) => setNewRecord({ ...newRecord, completedDate: e?.target?.value })}
            />
          </div>

          <div className="flex justify-end space-x-2 mt-4">
            <Button variant="ghost" onClick={() => setShowAddRecord(false)}>
              Cancel
            </Button>
            <Button variant="default" onClick={handleAddRecord}>
              Add Record
            </Button>
          </div>
        </div>
      )}
      {/* Maintenance Records List */}
      <div className="space-y-4">
        {maintenanceRecords?.length === 0 ? (
          <div className="text-center py-8">
            <Icon name="Wrench" size={48} className="text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">No maintenance records found</p>
          </div>
        ) : (
          maintenanceRecords?.map((record) => (
            <div key={record?.id} className="border border-border rounded-lg p-4 hover:bg-muted/50 transition-smooth">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                    <Icon name={getTypeIcon(record?.type)} size={18} className="text-primary" />
                  </div>
                  <div>
                    <h5 className="font-medium text-foreground">{record?.description}</h5>
                    <p className="text-sm text-muted-foreground">
                      {maintenanceTypeOptions?.find(opt => opt?.value === record?.type)?.label}
                    </p>
                  </div>
                </div>
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(record?.status)}`}>
                  {statusOptions?.find(opt => opt?.value === record?.status)?.label}
                </span>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-sm mb-3">
                <div>
                  <p className="text-muted-foreground">Performed By</p>
                  <p className="font-medium text-foreground">{record?.performedBy || 'Not assigned'}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Scheduled</p>
                  <p className="font-medium text-foreground">{formatDate(record?.scheduledDate)}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Completed</p>
                  <p className="font-medium text-foreground">{formatDate(record?.completedDate)}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Cost</p>
                  <p className="font-medium text-foreground">
{record?.cost ? `₹${record?.cost?.toLocaleString('en-IN', { minimumFractionDigits: 2 })}` : 'No cost'}
                  </p>
                </div>
              </div>

              {record?.notes && (
                <div className="bg-muted/50 rounded-md p-3 mb-3">
                  <p className="text-sm text-foreground">{record?.notes}</p>
                </div>
              )}

              {record?.attachments && record?.attachments?.length > 0 && (
                <div className="flex items-center space-x-2">
                  <Icon name="Paperclip" size={14} className="text-muted-foreground" />
                  <div className="flex space-x-2">
                    {record?.attachments?.map((attachment, index) => (
                      <button
                        key={index}
                        className="text-xs text-accent hover:text-accent/80 underline"
                        onClick={() => console.log('Download:', attachment)}
                      >
                        {attachment}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default MaintenanceRecords;